import Dexie from 'dexie';
import { decodeRequestData } from '../utils/requestData';

/**
 * Локальная база данных для оффлайн режима
 * Использует Dexie (обертка над IndexedDB)
 */
class LocalDB extends Dexie {
  constructor() {
    super('LeprechaunCRM');

    // Определяем схему БД
    this.version(1).stores({
      // Таблица задач
      tasks: '++id, server_id, user_id, client_id, project_id, title, status, priority, due_date, completed_at, cost, points, category, color, address, is_recurring, recurrence_type, created_at, updated_at, synced_at, sync_status, local_created, local_updated',

      // Таблица клиентов
      clients: '++id, server_id, user_id, name, email, phone, company, notes, rating, created_at, updated_at, synced_at, sync_status, local_created, local_updated',

      // Таблица финансов
      finances: '++id, server_id, user_id, task_id, client_id, type, amount, category, description, date, created_at, synced_at, sync_status, local_created, local_updated',

      // Таблица проектов
      projects: '++id, server_id, user_id, name, description, client_id, status, created_at, updated_at, synced_at, sync_status, local_created, local_updated',

      // Таблица очереди синхронизации (запросы, которые нужно отправить на сервер)
      syncQueue: '++id, method, url, data, headers, timestamp, retries, error, priority',

      // Таблица пользователей
      users: '++id, server_id, email, name, full_name, display_name, role, created_at, updated_at',

      // Таблица настроек синхронизации
      syncSettings: 'key, value'
    });

    this.version(2).stores({
      tasks: '++id, server_id, user_id, client_id, object_id, project_id, title, status, priority, due_date, completed_at, cost, points, category, color, address, is_recurring, recurrence_type, created_at, updated_at, synced_at, sync_status, local_created, local_updated'
    });

    this.version(3).stores({
      tasks: '++id, server_id, &temp_id, user_id, client_id, object_id, project_id, title, status, priority, due_date, completed_at, cost, points, category, color, address, is_recurring, recurrence_type, created_at, updated_at, synced_at, sync_status, local_created, local_updated',
      syncQueue: '++id, &operation_id, entity_type, entity_local_id, method, url, status, next_retry_at, created_at, updated_at, retries, priority'
    }).upgrade(async (transaction) => {
      const queue = transaction.table('syncQueue');
      await queue.toCollection().modify((item) => {
        item.operation_id = item.operation_id || `legacy_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        item.status = item.status || 'pending';
        item.created_at = item.created_at || item.timestamp || new Date().toISOString();
        item.updated_at = item.updated_at || item.created_at;
        item.next_retry_at = item.next_retry_at || null;
      });
    });

    this.version(4).stores({
      tasks: '++id, server_id, &temp_id, user_id, client_id, object_id, project_id, title, status, priority, due_date, completed_at, cost, points, category, color, address, is_personal, is_recurring, recurrence_type, created_at, updated_at, synced_at, sync_status, local_created, local_updated',
    }).upgrade(async (transaction) => {
      await transaction.table('tasks').toCollection().modify((task) => {
        if (task.is_personal == null) task.is_personal = false;
      });
    });

    // Инициализация таблиц
    this.tasks = this.table('tasks');
    this.clients = this.table('clients');
    this.finances = this.table('finances');
    this.projects = this.table('projects');
    this.syncQueue = this.table('syncQueue');
    this.users = this.table('users');
    this.syncSettings = this.table('syncSettings');
  }

  /**
   * Инициализация БД
   */
  async init() {
    try {
      // Пытаемся открыть БД с retry логикой
      let retries = 3;
      let lastError = null;

      while (retries > 0) {
        try {
          await this.open();
          console.log('Local DB initialized');

          // Устанавливаем настройки только при первом запуске, не стирая историю.
          if ((await this.syncSettings.get('lastSync')) == null) {
            await this.setSyncSetting('lastSync', null);
          }
          if ((await this.syncSettings.get('autoSync')) == null) {
            await this.setSyncSetting('autoSync', true);
          }
          if ((await this.syncSettings.get('syncInterval')) == null) {
            await this.setSyncSetting('syncInterval', 30000);
          }

          return true;
        } catch (openError) {
          lastError = openError;
          const errorName = String(openError?.name || '');
          const innerName = String(openError?.inner?.name || openError?._e?.name || '');
          const errorMessage = String(openError?.message || '');

          // Проверяем, не является ли это UnknownError или DatabaseClosedError
          const isKnownError = errorName === 'UnknownError' ||
                              errorName === 'DatabaseClosedError' ||
                              innerName === 'UnknownError' ||
                              errorMessage.includes('UnknownError') ||
                              errorMessage.includes('DatabaseClosedError') ||
                              errorMessage.includes('backing store');

          // Для известных ошибок Dexie делаем retry
          if (isKnownError && retries > 1) {
            retries--;
            // Ждем немного перед повторной попыткой
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }

          // Если это не известная ошибка или закончились попытки, пробрасываем
          throw openError;
        }
      }

      // Если все попытки не удались, но это известная ошибка - не логируем
      const errorName = String(lastError?.name || '');
      const innerName = String(lastError?.inner?.name || lastError?._e?.name || '');
      const errorMessage = String(lastError?.message || '');

      const isKnownError = errorName === 'UnknownError' ||
                          errorName === 'DatabaseClosedError' ||
                          innerName === 'UnknownError' ||
                          errorMessage.includes('UnknownError') ||
                          errorMessage.includes('DatabaseClosedError') ||
                          errorMessage.includes('backing store');

      if (!isKnownError) {
        console.error('Error initializing Local DB:', lastError);
      } else {
        // Для известных ошибок просто предупреждаем
        console.warn('Local DB initialization failed (known browser issue), continuing without offline mode');
      }

      return false;
    } catch (error) {
      const errorName = String(error?.name || '');
      const innerName = String(error?.inner?.name || error?._e?.name || '');
      const errorMessage = String(error?.message || '');

      const isKnownError = errorName === 'UnknownError' ||
                          errorName === 'DatabaseClosedError' ||
                          innerName === 'UnknownError' ||
                          errorMessage.includes('UnknownError') ||
                          errorMessage.includes('DatabaseClosedError') ||
                          errorMessage.includes('backing store');

      if (!isKnownError) {
        console.error('Error initializing Local DB:', error);
      }
      return false;
    }
  }

  /**
   * Получить настройку синхронизации
   */
  async getSyncSetting(key, defaultValue = null) {
    try {
      const setting = await this.syncSettings.get(key);
      return setting ? setting.value : defaultValue;
    } catch (error) {
      console.error('Error getting sync setting:', error);
      return defaultValue;
    }
  }

  /**
   * Установить настройку синхронизации
   */
  async setSyncSetting(key, value) {
    try {
      await this.syncSettings.put({ key, value });
    } catch (error) {
      console.error('Error setting sync setting:', error);
    }
  }

  /**
   * Добавить задачу в локальную БД
   */
  async addTask(task, isLocal = false, forceServer = false) {
    const normalizedTask = decodeRequestData(task);
    if (!normalizedTask || typeof normalizedTask !== 'object' || Array.isArray(normalizedTask)) {
      throw new TypeError('Task must be a JSON object');
    }
    task = normalizedTask;
    // Проверяем, что БД открыта
    if (!this.isOpen()) {
      try {
        await this.open();
      } catch (openError) {
        // Если не удалось открыть, просто возвращаем null (оффлайн режим недоступен)
        return null;
      }
    }

    try {
      const numericServerId = Number(task.server_id ?? task.id);
      const hasServerId = Number.isInteger(numericServerId) && numericServerId > 0;
      const taskData = {
        ...task,
        server_id: hasServerId ? numericServerId : null,
        temp_id: task.temp_id || (!hasServerId && typeof task.id === 'string' ? task.id : null),
        local_created: isLocal,
        local_updated: isLocal,
        synced_at: isLocal ? null : new Date().toISOString(),
        sync_status: isLocal ? 'pending' : 'synced',
        created_at: task.created_at || new Date().toISOString()
      };

      // Если задача уже существует (по server_id), обновляем её
      if (taskData.server_id) {
        const existing = await this.tasks.where('server_id').equals(taskData.server_id).first();
        if (existing) {
          // Не затираем локальное изменение свежей серверной копией до отправки очереди.
          if (!isLocal && !forceServer && existing.sync_status === 'pending') {
            return existing.id;
          }
          // Обновляем существующую запись
          await this.tasks.update(existing.id, taskData);
          return existing.id;
        }
      }

      // Если задача создана локально, используем временный ID
      if (isLocal && !taskData.server_id && !taskData.temp_id) {
        taskData.temp_id = `temp_${Date.now()}_${Math.random()}`;
      }

      // Ищем существующую запись по server_id или temp_id
      let existingTask = null;

      if (taskData.server_id) {
        existingTask = await this.tasks.where('server_id').equals(taskData.server_id).first();
      } else if (taskData.temp_id) {
        existingTask = await this.tasks.where('temp_id').equals(taskData.temp_id).first();
      }

      if (existingTask) {
        // Обновляем существующую запись
        await this.tasks.update(existingTask.id, taskData);
        return existingTask.id;
      }

      // Если записи нет, пытаемся добавить
      // Используем try-catch для обработки возможных ошибок дублирования
      try {
        return await this.tasks.add(taskData);
      } catch (addError) {
        // Функция для проверки ConstraintError - более агрессивная проверка
        const checkConstraintError = (err) => {
          if (!err) return false;

          // Проверяем все возможные варианты
          const name = String(err.name || '');
          const message = String(err.message || '');
          const inner = err.inner || err._e;
          const innerName = inner ? String(inner.name || '') : '';
          const innerMessage = inner ? String(inner.message || '') : '';

          // Проверяем все варианты
          if (name === 'ConstraintError' || innerName === 'ConstraintError') return true;
          if (message.includes('ConstraintError')) return true;
          if (message.includes('already exists')) return true;
          if (message.includes('Key already exists')) return true;
          if (innerMessage.includes('ConstraintError')) return true;
          if (innerMessage.includes('already exists')) return true;
          if (innerMessage.includes('Key already exists')) return true;

          return false;
        };

        const isConstraintError = checkConstraintError(addError);

        // Если это ConstraintError, пытаемся найти и обновить
        if (isConstraintError) {
          try {
            if (taskData.server_id) {
              existingTask = await this.tasks.where('server_id').equals(taskData.server_id).first();
            }

            if (existingTask) {
              await this.tasks.update(existingTask.id, taskData);
              return existingTask.id;
            }
          } catch (updateError) {
            // Игнорируем ошибки обновления
          }

          // НЕ логируем ConstraintError - просто возвращаем null
          return null;
        }

        // Для других ошибок логируем
        console.error('Error adding task to local DB (non-constraint):', addError);
        return null;
      }
    } catch (error) {
      // Функция для проверки ConstraintError
      const checkConstraintError = (err) => {
        if (!err) return false;
        const name = String(err.name || '');
        const message = String(err.message || '');
        const inner = err.inner || err._e;
        const innerName = inner ? String(inner.name || '') : '';
        const innerMessage = inner ? String(inner.message || '') : '';

        if (name === 'ConstraintError' || innerName === 'ConstraintError') return true;
        if (message.includes('ConstraintError')) return true;
        if (message.includes('already exists')) return true;
        if (message.includes('Key already exists')) return true;
        if (innerMessage.includes('ConstraintError')) return true;
        if (innerMessage.includes('already exists')) return true;
        if (innerMessage.includes('Key already exists')) return true;
        return false;
      };

      const isConstraintError = checkConstraintError(error);

      // НЕ логируем ConstraintError
      if (!isConstraintError) {
        console.error('Error adding task to local DB:', error);
      }

      // Проверяем, не является ли это ошибкой закрытой БД
      const errorName = String(error?.name || '');
      const innerName = String(error?.inner?.name || error?._e?.name || '');
      const errorMessage = String(error?.message || '');

      const isKnownError = errorName === 'UnknownError' ||
                          errorName === 'DatabaseClosedError' ||
                          innerName === 'UnknownError' ||
                          errorMessage.includes('UnknownError') ||
                          errorMessage.includes('DatabaseClosedError') ||
                          errorMessage.includes('backing store');

      // Проверяем также ConstraintError
      const errorNameForCheck = String(error?.name || '');
      const innerErrorForCheck = error?.inner || error?._e;
      const innerNameForCheck = innerErrorForCheck ? String(innerErrorForCheck.name || '') : '';
      const errorMessageForCheck = String(error?.message || '');
      const innerMessageForCheck = innerErrorForCheck ? String(innerErrorForCheck.message || '') : '';

      const isConstraint = errorNameForCheck === 'ConstraintError' ||
                          innerNameForCheck === 'ConstraintError' ||
                          errorMessageForCheck.includes('ConstraintError') ||
                          errorMessageForCheck.includes('already exists') ||
                          errorMessageForCheck.includes('Key already exists') ||
                          innerMessageForCheck.includes('ConstraintError') ||
                          innerMessageForCheck.includes('already exists') ||
                          innerMessageForCheck.includes('Key already exists');

      // НЕ логируем известные ошибки IndexedDB и ConstraintError
      if (!isKnownError && !isConstraint) {
        console.error('Error adding task to local DB:', error);
      }

      // Возвращаем null вместо проброса ошибки
      return null;
    }
  }

  /**
   * Получить все задачи из локальной БД
   */
  async getTasks(userId = null) {
    try {
      if (userId) {
        return await this.tasks.where('user_id').equals(userId).toArray();
      }
      return await this.tasks.toArray();
    } catch (error) {
      console.error('Error getting tasks from local DB:', error);
      return [];
    }
  }

  /**
   * Получить задачу по серверному или временному ID.
   */
  async getTask(taskId) {
    if (!this.isOpen()) await this.open();
    const raw = String(taskId ?? '');
    if (raw.startsWith('offline_') || raw.startsWith('temp_')) {
      return this.tasks.where('temp_id').equals(raw).first();
    }
    const numericId = Number(raw);
    if (Number.isInteger(numericId) && numericId > 0) {
      return (await this.tasks.where('server_id').equals(numericId).first())
        || this.tasks.get(numericId);
    }
    return null;
  }

  /**
   * Создать оптимистичную локальную задачу, которая ещё не существует на сервере.
   */
  async addPendingTask(task, operationId) {
    if (!this.isOpen()) await this.open();
    const normalizedTask = decodeRequestData(task);
    if (!normalizedTask || typeof normalizedTask !== 'object' || Array.isArray(normalizedTask)) {
      throw new TypeError('Pending task must be a JSON object');
    }
    task = normalizedTask;
    const tempId = `offline_${operationId}`;
    const now = new Date().toISOString();
    const record = {
      ...task,
      server_id: null,
      temp_id: tempId,
      local_created: true,
      local_updated: true,
      sync_status: 'pending',
      sync_operation_id: operationId,
      synced_at: null,
      created_at: task.created_at || now,
      updated_at: now,
    };
    const id = await this.tasks.add(record);
    return { ...record, id };
  }

  /**
   * Заменить временную задачу серверной после успешного POST /tasks.
   */
  async resolvePendingTask(tempId, serverTask) {
    if (!this.isOpen()) await this.open();
    const pending = await this.tasks.where('temp_id').equals(tempId).first();
    if (pending) {
      await this.tasks.delete(pending.id);
    }
    await this.addTask(serverTask, false);
    return this.getTask(serverTask.id);
  }

  /**
   * Обновить задачу в локальной БД
   */
  async updateTask(taskId, updates, isLocal = false) {
    try {
      const updateData = {
        ...updates,
        local_updated: isLocal,
        synced_at: isLocal ? null : new Date().toISOString(),
        sync_status: isLocal ? 'pending' : 'synced'
      };

      // Ищем задачу по server_id или локальному ID
      let task;
      if (typeof taskId === 'string' && (taskId.startsWith('offline_') || taskId.startsWith('temp_'))) {
        task = await this.tasks.where('temp_id').equals(taskId).first();
      } else if (typeof taskId === 'number' || typeof taskId === 'string') {
        task = await this.tasks.where('server_id').equals(parseInt(taskId)).first();
      }

      if (!task) {
        task = await this.tasks.get(parseInt(taskId));
      }

      if (task) {
        return await this.tasks.update(task.id, updateData);
      }

      throw new Error('Task not found');
    } catch (error) {
      console.error('Error updating task in local DB:', error);
      throw error;
    }
  }

  /**
   * Удалить задачу из локальной БД
   */
  async deleteTask(taskId) {
    try {
      // Ищем задачу по server_id или локальному ID
      let task;
      if (typeof taskId === 'string' && (taskId.startsWith('offline_') || taskId.startsWith('temp_'))) {
        task = await this.tasks.where('temp_id').equals(taskId).first();
      } else if (typeof taskId === 'number' || typeof taskId === 'string') {
        task = await this.tasks.where('server_id').equals(parseInt(taskId)).first();
      }

      if (!task) {
        task = await this.tasks.get(parseInt(taskId));
      }

      if (task) {
        return await this.tasks.delete(task.id);
      }

      throw new Error('Task not found');
    } catch (error) {
      console.error('Error deleting task from local DB:', error);
      throw error;
    }
  }

  /**
   * Очистить все пользовательские данные в локальной БД (после очистки на сервере)
   */
  async clearAllUserData() {
    try {
      if (!this.isOpen()) await this.open();
      await this.tasks.clear();
      await this.clients.clear();
      await this.finances.clear();
      await this.projects.clear();
      await this.syncQueue.clear();
      await this.setSyncSetting('lastSync', null);
      return true;
    } catch (error) {
      console.error('Error clearing local DB:', error);
      throw error;
    }
  }

  /**
   * Добавить клиента в локальную БД
   */
  async addClient(client, isLocal = false) {
    // Проверяем, что БД открыта
    if (!this.isOpen()) {
      try {
        await this.open();
      } catch (openError) {
        return null;
      }
    }

    try {
      const clientData = {
        ...client,
        server_id: client.id || null,
        local_created: isLocal,
        local_updated: isLocal,
        synced_at: isLocal ? null : new Date().toISOString(),
        sync_status: isLocal ? 'pending' : 'synced'
      };

      if (clientData.server_id) {
        const existing = await this.clients.where('server_id').equals(clientData.server_id).first();
        if (existing) {
          await this.clients.update(existing.id, clientData);
          return existing.id;
        }
      }

      if (isLocal && !clientData.server_id) {
        clientData.temp_id = `temp_${Date.now()}_${Math.random()}`;
      }

      // Проверяем существование перед добавлением
      let existingClient = null;
      if (clientData.server_id) {
        existingClient = await this.clients.where('server_id').equals(clientData.server_id).first();
      } else if (clientData.temp_id) {
        existingClient = await this.clients.where('temp_id').equals(clientData.temp_id).first();
      }

      if (existingClient) {
        await this.clients.update(existingClient.id, clientData);
        return existingClient.id;
      }

      // Пытаемся добавить новую запись
      try {
        return await this.clients.add(clientData);
      } catch (addError) {
        // Функция для проверки ConstraintError
        const isConstraintError = (err) => {
          if (!err) return false;
          const errorMessage = String(err.message || '');
          const errorName = String(err.name || '');
          const innerError = err.inner || err._e;
          const innerName = innerError ? String(innerError.name || '') : '';
          const innerMessage = innerError ? String(innerError.message || '') : '';
          return errorName === 'ConstraintError' ||
                 innerName === 'ConstraintError' ||
                 errorMessage.includes('already exists') ||
                 errorMessage.includes('Key already exists') ||
                 errorMessage.includes('ConstraintError') ||
                 innerMessage.includes('already exists') ||
                 innerMessage.includes('Key already exists') ||
                 innerMessage.includes('ConstraintError');
        };

        if (isConstraintError(addError)) {
          if (clientData.server_id) {
            existingClient = await this.clients.where('server_id').equals(clientData.server_id).first();
          }

          if (existingClient) {
            await this.clients.update(existingClient.id, clientData);
            return existingClient.id;
          }
          // НЕ логируем ConstraintError
          return null;
        }

        console.error('Error adding client to local DB (non-constraint):', addError);
        return null;
      }
    } catch (error) {
      const isConstraintError = (err) => {
        if (!err) return false;
        const errorMessage = String(err.message || '');
        const errorName = String(err.name || '');
        const innerError = err.inner || err._e;
        const innerName = innerError ? String(innerError.name || '') : '';
        const innerMessage = innerError ? String(innerError.message || '') : '';
        return errorName === 'ConstraintError' ||
               innerName === 'ConstraintError' ||
               errorMessage.includes('already exists') ||
               errorMessage.includes('Key already exists') ||
               errorMessage.includes('ConstraintError') ||
               innerMessage.includes('already exists') ||
               innerMessage.includes('Key already exists') ||
               innerMessage.includes('ConstraintError');
      };

      // Проверяем, не является ли это ошибкой закрытой БД
      const errorName = String(error?.name || '');
      const innerName = String(error?.inner?.name || error?._e?.name || '');
      const errorMessage = String(error?.message || '');

      const isKnownError = errorName === 'UnknownError' ||
                          errorName === 'DatabaseClosedError' ||
                          innerName === 'UnknownError' ||
                          errorMessage.includes('UnknownError') ||
                          errorMessage.includes('DatabaseClosedError') ||
                          errorMessage.includes('backing store');

      const isConstraint = isConstraintError(error);

      // НЕ логируем известные ошибки IndexedDB и ConstraintError
      if (!isKnownError && !isConstraint) {
        console.error('Error adding client to local DB:', error);
      }
      return null;
    }
  }

  /**
   * Получить всех клиентов из локальной БД
   */
  async getClients(userId = null) {
    try {
      if (userId) {
        return await this.clients.where('user_id').equals(userId).toArray();
      }
      return await this.clients.toArray();
    } catch (error) {
      console.error('Error getting clients from local DB:', error);
      return [];
    }
  }

  /**
   * Добавить финансовую запись в локальную БД
   */
  async addFinance(finance, isLocal = false) {
    // Проверяем, что БД открыта
    if (!this.isOpen()) {
      try {
        await this.open();
      } catch (openError) {
        return null;
      }
    }

    try {
      const financeData = {
        ...finance,
        server_id: finance.id || null,
        local_created: isLocal,
        local_updated: isLocal,
        synced_at: isLocal ? null : new Date().toISOString(),
        sync_status: isLocal ? 'pending' : 'synced'
      };

      if (financeData.server_id) {
        const existing = await this.finances.where('server_id').equals(financeData.server_id).first();
        if (existing) {
          await this.finances.update(existing.id, financeData);
          return existing.id;
        }
      }

      if (isLocal && !financeData.server_id) {
        financeData.temp_id = `temp_${Date.now()}_${Math.random()}`;
      }

      // Проверяем существование перед добавлением
      let existingFinance = null;
      if (financeData.server_id) {
        existingFinance = await this.finances.where('server_id').equals(financeData.server_id).first();
      } else if (financeData.temp_id) {
        existingFinance = await this.finances.where('temp_id').equals(financeData.temp_id).first();
      }

      if (existingFinance) {
        await this.finances.update(existingFinance.id, financeData);
        return existingFinance.id;
      }

      // Пытаемся добавить новую запись
      try {
        return await this.finances.add(financeData);
      } catch (addError) {
        // Функция для проверки ConstraintError
        const isConstraintError = (err) => {
          if (!err) return false;
          const errorMessage = String(err.message || '');
          const errorName = String(err.name || '');
          const innerError = err.inner || err._e;
          const innerName = innerError ? String(innerError.name || '') : '';
          const innerMessage = innerError ? String(innerError.message || '') : '';
          return errorName === 'ConstraintError' ||
                 innerName === 'ConstraintError' ||
                 errorMessage.includes('already exists') ||
                 errorMessage.includes('Key already exists') ||
                 errorMessage.includes('ConstraintError') ||
                 innerMessage.includes('already exists') ||
                 innerMessage.includes('Key already exists') ||
                 innerMessage.includes('ConstraintError');
        };

        if (isConstraintError(addError)) {
          if (financeData.server_id) {
            existingFinance = await this.finances.where('server_id').equals(financeData.server_id).first();
          }

          if (existingFinance) {
            await this.finances.update(existingFinance.id, financeData);
            return existingFinance.id;
          }
          // НЕ логируем ConstraintError
          return null;
        }

        console.error('Error adding finance to local DB (non-constraint):', addError);
        return null;
      }
    } catch (error) {
      const isConstraintError = (err) => {
        if (!err) return false;
        const errorMessage = String(err.message || '');
        const errorName = String(err.name || '');
        const innerError = err.inner || err._e;
        const innerName = innerError ? String(innerError.name || '') : '';
        const innerMessage = innerError ? String(innerError.message || '') : '';
        return errorName === 'ConstraintError' ||
               innerName === 'ConstraintError' ||
               errorMessage.includes('already exists') ||
               errorMessage.includes('Key already exists') ||
               errorMessage.includes('ConstraintError') ||
               innerMessage.includes('already exists') ||
               innerMessage.includes('Key already exists') ||
               innerMessage.includes('ConstraintError');
      };

      // Проверяем, не является ли это ошибкой закрытой БД
      const errorName = String(error?.name || '');
      const innerName = String(error?.inner?.name || error?._e?.name || '');
      const errorMessage = String(error?.message || '');

      const isKnownError = errorName === 'UnknownError' ||
                          errorName === 'DatabaseClosedError' ||
                          innerName === 'UnknownError' ||
                          errorMessage.includes('UnknownError') ||
                          errorMessage.includes('DatabaseClosedError') ||
                          errorMessage.includes('backing store');

      const isConstraint = isConstraintError(error);

      // НЕ логируем известные ошибки IndexedDB и ConstraintError
      if (!isKnownError && !isConstraint) {
        console.error('Error adding finance to local DB:', error);
      }
      return null;
    }
  }

  /**
   * Получить все финансовые записи из локальной БД
   */
  async getFinances(userId = null) {
    try {
      if (userId) {
        return await this.finances.where('user_id').equals(userId).toArray();
      }
      return await this.finances.toArray();
    } catch (error) {
      console.error('Error getting finances from local DB:', error);
      return [];
    }
  }

  /**
   * Добавить запрос в очередь синхронизации
   */
  async addToSyncQueue(method, url, data, headers = {}, priority = 0, metadata = {}) {
    try {
      const now = new Date().toISOString();
      const operationId = metadata.operation_id || `op_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const existing = await this.syncQueue.where('operation_id').equals(operationId).first();
      if (existing) return existing.id;
      return await this.syncQueue.add({
        ...metadata,
        operation_id: operationId,
        method,
        url,
        data: decodeRequestData(data),
        headers,
        timestamp: now,
        created_at: now,
        updated_at: now,
        next_retry_at: null,
        status: 'pending',
        retries: 0,
        error: null,
        priority
      });
    } catch (error) {
      console.error('Error adding to sync queue:', error);
      throw error;
    }
  }

  /**
   * Получить все запросы из очереди синхронизации
   */
  async getSyncQueue() {
    try {
      const items = await this.syncQueue.toArray();
      return items.sort((a, b) => {
        const priorityDiff = Number(b.priority || 0) - Number(a.priority || 0);
        if (priorityDiff) return priorityDiff;
        return String(a.created_at || a.timestamp || '').localeCompare(String(b.created_at || b.timestamp || ''));
      });
    } catch (error) {
      console.error('Error getting sync queue:', error);
      return [];
    }
  }

  /**
   * Очистить всю очередь синхронизации
   */
  async clearSyncQueue() {
    try {
      await this.syncQueue.clear();
    } catch (error) {
      console.error('Error clearing sync queue:', error);
      throw error;
    }
  }

  /**
   * Удалить запрос из очереди синхронизации
   */
  async removeFromSyncQueue(queueId) {
    try {
      return await this.syncQueue.delete(queueId);
    } catch (error) {
      console.error('Error removing from sync queue:', error);
      throw error;
    }
  }

  /**
   * Обновить запрос в очереди синхронизации
   */
  async updateSyncQueueItem(queueId, updates) {
    try {
      return await this.syncQueue.update(queueId, updates);
    } catch (error) {
      console.error('Error updating sync queue item:', error);
      throw error;
    }
  }

  async getQueueItemByOperationId(operationId) {
    if (!this.isOpen()) await this.open();
    return this.syncQueue.where('operation_id').equals(operationId).first();
  }

  async getQueueItemByLocalEntity(entityType, entityLocalId) {
    if (!this.isOpen()) await this.open();
    return this.syncQueue
      .filter((item) => item.entity_type === entityType && item.entity_local_id === entityLocalId)
      .first();
  }

  /**
   * Очистить старые синхронизированные записи (опционально)
   */
  async cleanOldSyncedData(daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      // Удаляем старые синхронизированные записи (опционально)
      // Это можно использовать для освобождения места
      // Пока не реализуем, чтобы сохранить историю
    } catch (error) {
      console.error('Error cleaning old synced data:', error);
    }
  }

  /**
   * Только число запросов в очереди (для надписи «N в очереди» и кнопки «Очистить»).
   */
  async getQueueCount() {
    try {
      if (!this.isOpen()) return 0;
      return await this.syncQueue.count();
    } catch (error) {
      return 0;
    }
  }

  /**
   * Получить количество несинхронизированных записей (задачи + клиенты + финансы + очередь).
   */
  async getPendingSyncCount() {
    try {
      if (!this.isOpen()) return 0;
      const tasks = await this.tasks.where('sync_status').equals('pending').count();
      const clients = await this.clients.where('sync_status').equals('pending').count();
      const finances = await this.finances.where('sync_status').equals('pending').count();
      const queue = await this.syncQueue.count();
      return tasks + clients + finances + queue;
    } catch (error) {
      // Проверяем, не является ли это ошибкой закрытой БД
      const errorName = String(error?.name || '');
      const innerName = String(error?.inner?.name || error?._e?.name || '');
      const errorMessage = String(error?.message || '');

      const isKnownError = errorName === 'UnknownError' ||
                          errorName === 'DatabaseClosedError' ||
                          innerName === 'UnknownError' ||
                          errorMessage.includes('UnknownError') ||
                          errorMessage.includes('DatabaseClosedError') ||
                          errorMessage.includes('backing store');

      if (!isKnownError) {
        console.error('Error getting pending sync count:', error);
      }
      return 0;
    }
  }
}

// Создаем единственный экземпляр БД
const localDB = new LocalDB();

export default localDB;
